/**
 * waTime — one canonical time representation for the chat list.
 *
 * WHY THIS EXISTS
 * The conversation list mixes timestamps from two producers that do not
 * agree on an encoding, and then compared them as raw strings:
 *
 *   • the server writes `datetime.now().isoformat()` — a **naive**
 *     string with no offset, denoting the wall clock of the host that
 *     wrote it (`routers/infobip.py:604,621`, `routers/whatsapp.py`).
 *     All 15,307 rows of production `chats.json` carry this shape and
 *     only this shape (census 2026-09-01);
 *   • the realtime patch writes `new Date(...).toISOString()` — UTC
 *     with a `Z` (`useChatListRealtime.js:74`).
 *
 * `"2026-09-01T10:30:00.000Z"` < `"2026-09-01T12:00:00.123456"` as
 * strings, while as instants the first is the *later* of the two. So a
 * brand-new message sorted its conversation two hours into the past —
 * the row moved down — and the next refetch, which replaced the `Z`
 * string with the server's naive one, threw it back to the top. That is
 * the conversation "moving twice".
 *
 * THE CANONICAL REPRESENTATION: epoch milliseconds, UTC, as a number.
 * Everything is read into it before anything is compared. A number has
 * no encoding left to disagree about, and it makes two spellings of one
 * instant compare *equal* rather than merely close.
 *
 * READING A NAIVE STRING IS THE WHOLE PROBLEM
 * There are three ways to read a string that carries no offset, and two
 * of them are wrong here:
 *
 *   1. as UTC — this is the bug, unchanged. The naive strings are the
 *      host's wall clock; calling them UTC dates every server row two
 *      hours into the future and a new message still sorts below them.
 *   2. as the *browser's* local zone — what bare `new Date(s)` does, and
 *      what `format.js:_toDate` still does. It happens to look right for
 *      an agent in Israel (+0300 in summer, one hour off, in the safe
 *      direction) and reproduces the original defect outright for a
 *      browser west of the writer: in UTC the same string reads two
 *      hours in the future again.
 *   3. as the wall clock of the host that wrote it — the only reading
 *      that inverts the writer. That host is `Europe/Berlin`
 *      (`timedatectl` on 168.119.151.103: `Europe/Berlin (CEST, +0200)`,
 *      read 2026-09-01), so that is what we declare, by IANA name rather
 *      than by a fixed `+02:00`, so the DST rule that applied on the
 *      date in question is the one used. Israel is +0300 in summer while
 *      the writer is +0200 — the gap is two hours, not three.
 *
 * The backend's own reader makes exactly this distinction:
 * `backend/shared/wa_timestamps.py` `as_utc(..., naive_is_local=True)`
 * for `chats.json`, where `.astimezone()` inverts the writer for free
 * because it runs on that same host. In a browser it cannot be had for
 * free, so the zone is named here — see SERVER_WALL_CLOCK_TZ.
 */

/**
 * The IANA zone whose wall clock the server's offset-less timestamps
 * are written in. It is a property of the *writer's host*, not of the
 * business: if the backend host ever moves zones (or is normalised to
 * UTC, which would be the real fix), this constant moves with it and
 * `waTime.test.js` states that coupling explicitly.
 */
export const SERVER_WALL_CLOCK_TZ = 'Europe/Berlin';

// Epochs at or below this are seconds, above are milliseconds. Same
// threshold the four existing copies of this check already use
// (useChatListRealtime.js:74, MessagesViewport.js:38, format.js:24).
const MS_THRESHOLD = 1e12;

// Date, optional time, optional fractional seconds, optional offset in
// any of the three spellings that reach us: `Z`, `+03:00`, `+0000`.
// Parsed by hand rather than handed to `Date.parse` so that the naive
// case is never silently resolved against the browser's zone, and so
// that more than three fractional digits (the server writes six) are
// handled identically on every engine.
const ISO_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:[.,](\d+))?)?\s*(Z|z|[+-]\d{2}:?\d{2})?$/;

const _formatters = new Map();

const _formatterFor = (timeZone) => {
  let f = _formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    _formatters.set(timeZone, f);
  }
  return f;
};

// 2026-11-02 (workspace audit, measured): a supervisor's «הכל» list is ~14k
// rows, and every naive string cost two `formatToParts` calls (~7µs each) on
// EACH comparison — 5.5s per sort, on every click, keystroke and realtime
// patch. The offset is cached per 15-minute UTC bucket (every real DST
// transition falls on a quarter hour), and a parsed string is cached whole.
const OFFSET_BUCKET_MS = 15 * 60 * 1000;
const _offsetCache = new Map();
const _parsedCache = new Map();
const CACHE_MAX = 50000;

const _remember = (cache, key, value) => {
  if (cache.size >= CACHE_MAX) cache.clear();
  cache.set(key, value);
  return value;
};

/** Offset of `timeZone` from UTC, in ms, at the instant `epochMs`. */
const _zoneOffsetMsAt = (timeZone, epochMs) => {
  const key = `${timeZone}|${Math.floor(epochMs / OFFSET_BUCKET_MS)}`;
  const hit = _offsetCache.get(key);
  if (hit !== undefined) return hit;
  return _remember(_offsetCache, key, _zoneOffsetMsAtUncached(timeZone, epochMs));
};

const _zoneOffsetMsAtUncached = (timeZone, epochMs) => {
  const parts = {};
  for (const p of _formatterFor(timeZone).formatToParts(epochMs)) {
    if (p.type !== 'literal') parts[p.type] = p.value;
  }
  const hour = Number(parts.hour) % 24;   // h23 should not emit 24, belt and braces
  const asIfUTC = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    hour, Number(parts.minute), Number(parts.second),
  );
  // formatToParts has no ms field, so compare against the whole second.
  return asIfUTC - Math.floor(epochMs / 1000) * 1000;
};

/**
 * A wall-clock reading in `timeZone` → epoch ms.
 *
 * Two passes: guess that the wall clock is UTC, look up the zone's
 * offset near that instant, correct, then re-check the offset at the
 * corrected instant so a reading that lands on the far side of a DST
 * transition is still resolved with the offset that actually applied.
 * The one hour that occurs twice on a fall-back night is ambiguous by
 * construction; we resolve it to the first (pre-transition) instant,
 * which is the same choice `datetime.astimezone()` makes on the server.
 */
const _wallClockToEpochMs = (y, mo, d, h, mi, s, ms, timeZone) => {
  const guess = Date.UTC(y, mo - 1, d, h, mi, s, ms);
  if (Number.isNaN(guess)) return null;
  let offset;
  try {
    offset = _zoneOffsetMsAt(timeZone, guess);
    const corrected = guess - offset;
    const offsetThere = _zoneOffsetMsAt(timeZone, corrected);
    if (offsetThere !== offset) offset = offsetThere;
  } catch (err) {
    // No Intl / unknown zone: nothing better is available than the
    // engine's own local reading. Every browser we support has Intl
    // with IANA zones, so this is a floor, not a path we plan for.
    return new Date(y, mo - 1, d, h, mi, s, ms).getTime();
  }
  return guess - offset;
};

const _offsetStringToMs = (raw) => {
  if (raw === 'Z' || raw === 'z') return 0;
  const sign = raw[0] === '-' ? -1 : 1;
  const digits = raw.slice(1).replace(':', '');
  return sign * ((Number(digits.slice(0, 2)) * 60) + Number(digits.slice(2, 4))) * 60000;
};

/**
 * Read any timestamp this codebase stores into epoch milliseconds (UTC).
 *
 * Accepts epoch seconds or milliseconds, a `Date`, and ISO-8601 strings
 * with an offset (`Z`, `+03:00`, `+0000`) or without one. A string with
 * no offset is read as a wall clock in `serverTz` — see the module
 * header for why that, and not UTC and not the browser's zone.
 *
 * Returns `null` for anything empty or unparseable. Callers sort those
 * to the *bottom*: an unreadable timestamp is not evidence of recency.
 */
export function toEpochMs(value, serverTz = SERVER_WALL_CLOCK_TZ) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === 'boolean') return null;   // a flag is not a timestamp
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return null;
    return value > MS_THRESHOLD ? value : value * 1000;
  }
  if (typeof value !== 'string') return null;

  const s = value.trim();
  if (!s) return null;

  const cacheKey = `${serverTz}\u0000${s}`;
  const cached = _parsedCache.get(cacheKey);
  if (cached !== undefined) return cached;
  return _remember(_parsedCache, cacheKey, _parseString(s, serverTz));
}

const _parseString = (s, serverTz) => {
  const m = ISO_RE.exec(s);
  if (!m) {
    // Not a shape we recognise (RFC-1123, epoch-in-a-string, junk).
    // `Date.parse` resolves a naive value against the browser's zone,
    // which is exactly what this module exists to avoid — so only a
    // numeric string is rescued here, and everything else is `null`.
    if (/^\d+$/.test(s)) return toEpochMs(Number(s), serverTz);
    return null;
  }

  const [, yy, mo, dd, hh, mi, ss, frac, off] = m;
  const ms = frac ? Number((frac + '000').slice(0, 3)) : 0;
  const y = Number(yy);
  const mon = Number(mo);
  const day = Number(dd);
  const hour = hh === undefined ? 0 : Number(hh);
  const min = mi === undefined ? 0 : Number(mi);
  const sec = ss === undefined ? 0 : Number(ss);

  if (off) {
    const t = Date.UTC(y, mon - 1, day, hour, min, sec, ms) - _offsetStringToMs(off);
    return Number.isNaN(t) ? null : t;
  }

  // No offset. Note this covers the date-only form (`"2026-06-28"`,
  // still present in older rows) — read as midnight on the server's
  // wall clock, not the UTC midnight `new Date()` would give it.
  return _wallClockToEpochMs(y, mon, day, hour, min, sec, ms, serverTz);
};

/**
 * Sort key for a chat row: epoch ms, or `-Infinity` when there is no
 * readable timestamp so the row sorts last under a descending sort
 * (which is what the previous `|| ''` string key did).
 */
export function chatRecencyMs(chat) {
  if (!chat) return -Infinity;
  const raw = chat.lastMessageTime || chat.timestamp || null;
  const ms = toEpochMs(raw);
  return ms == null ? -Infinity : ms;
}

/**
 * Most-recent-first comparator for chat rows. Lives here rather than in
 * the provider so that both spellings of one instant are reduced to the
 * canonical number by the same code that produced them, and so the
 * timezone matrix in `sort-recent-timestamp.test.js` can exercise the
 * real comparator in a child process with `TZ` set — this module has no
 * imports, which is what makes that possible. Equal instants return 0,
 * so `Array.prototype.sort` (stable since ES2019) leaves them alone.
 */
export function compareChatRecency(a, b) {
  const ta = chatRecencyMs(a);
  const tb = chatRecencyMs(b);
  if (ta === tb) return 0;
  return ta < tb ? 1 : -1;
}

/**
 * `[...chats].sort(compareChatRecency)`, with each row's key read once
 * instead of on every comparison. Same order, ties included: equal keys
 * keep their input order, exactly as the stable comparator sort does.
 */
export function sortChatsByRecency(chats) {
  const keyed = chats.map((c, i) => [chatRecencyMs(c), i, c]);
  keyed.sort((a, b) => {
    if (a[0] === b[0]) return a[1] - b[1];
    return a[0] < b[0] ? 1 : -1;
  });
  return keyed.map((k) => k[2]);
}
